import { useState } from "react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { api } from "../api/client.js";
import { formatMinorAmount } from "../config/stripe.js";
import { getRequestErrorMessage } from "../Utils/formValidation.js";

export default function BuildOrderPaymentForm({
  summaryLabel,
  customer,
  billingAddress,
  clientSecret,
  paymentIntentId,
  amount,
  currency,
  returnPath,
  onSuccess,
  onProcessing,
  onBusyChange,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [elementReady, setElementReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const [error, setError] = useState("");

  const getBillingDetails = () => ({
    name: customer.fullName,
    email: customer.email,
    phone: customer.phone,
    address: {
      country: billingAddress.country.toUpperCase(),
      state: billingAddress.state,
      city: billingAddress.city,
      postal_code: billingAddress.zip,
      line1: billingAddress.line1,
      line2: billingAddress.line2 || undefined,
    },
  });

  const finalizeBuildOrder = async () => {
    const response = await api.buildOrders.finalize({ paymentIntentId });
    if (response.data.paymentStatus === "succeeded") {
      onSuccess(response.data);
    } else {
      onProcessing(response.data);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || processing) return;

    setProcessing(true);
    onBusyChange(true);
    setError("");
    try {
      const submitResult = await elements.submit();
      if (submitResult.error) {
        setError(
          submitResult.error.message ||
            "Please check your card details and try again.",
        );
        return;
      }

      const result = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          payment_method_data: {
            billing_details: getBillingDetails(),
          },
          return_url: `${window.location.origin}${returnPath || "/contact-us"}`,
        },
        redirect: "if_required",
      });

      if (result.error) {
        setError(
          result.error.message ||
            (result.error.type === "validation_error"
              ? "Please check your card details and try again."
              : "Your card payment could not be completed. Please try again."),
        );
        return;
      }

      if (
        result.paymentIntent?.status === "succeeded" ||
        result.paymentIntent?.status === "requires_capture"
      ) {
        setPaymentSucceeded(true);
        try {
          await finalizeBuildOrder();
        } catch (requestError) {
          setError(
            getRequestErrorMessage(
              requestError,
              "Your payment was authorized, but the order could not be confirmed. Please retry confirmation.",
            ),
          );
        }
        return;
      }

      if (result.paymentIntent?.status === "processing") {
        await finalizeBuildOrder();
        return;
      }

      if (result.paymentIntent?.status === "requires_action") {
        setError("Additional payment authentication is still required.");
        return;
      }

      if (result.paymentIntent?.status === "requires_payment_method") {
        setError("Payment was not completed. Please verify your card details.");
        return;
      }

      setError("Payment was not completed. Please verify your card details.");
    } catch (requestError) {
      setError(
        getRequestErrorMessage(
          requestError,
          "The payment service could not be reached. Please try again.",
        ),
      );
    } finally {
      setProcessing(false);
      onBusyChange(false);
    }
  };

  const retryConfirmation = async () => {
    setProcessing(true);
    onBusyChange(true);
    setError("");
    try {
      await finalizeBuildOrder();
    } catch (requestError) {
      setError(
        getRequestErrorMessage(
          requestError,
          "Confirmation is temporarily unavailable. Please try again.",
        ),
      );
    } finally {
      setProcessing(false);
      onBusyChange(false);
    }
  };

  return (
    <form className="purchase-payment-form" onSubmit={handleSubmit}>
      <div className="purchase-payment-summary">
        <span>{summaryLabel}</span>
        <strong>{formatMinorAmount(amount, currency)}</strong>
      </div>

      <div className="purchase-payment-element">
        <p className="purchase-payment-method">
          Visa, Mastercard, Amex, and Discover accepted
        </p>
        {!elementReady && (
          <p className="purchase-loading" role="status">
            Loading secure payment form...
          </p>
        )}
        <PaymentElement
          onReady={() => setElementReady(true)}
          onLoadError={() =>
            setError("The secure payment form could not be loaded.")
          }
          options={{
            wallets: {
              applePay: "never",
              googlePay: "never",
              link: "never",
            },
            defaultValues: {
              billingDetails: {
                name: customer.fullName,
                email: customer.email,
                phone: customer.phone,
                address: {
                  country: billingAddress.country.toUpperCase(),
                  state: billingAddress.state,
                  city: billingAddress.city,
                  postal_code: billingAddress.zip,
                  line1: billingAddress.line1,
                  line2: billingAddress.line2,
                },
              },
            },
          }}
        />
      </div>

      {error && (
        <p className="purchase-error" role="alert">
          {error}
        </p>
      )}

      {paymentSucceeded && error ? (
        <button
          type="button"
          className="purchase-primary"
          onClick={retryConfirmation}
          disabled={processing}
        >
          {processing ? "Confirming..." : "Retry confirmation"}
        </button>
      ) : (
        <button
          type="submit"
          className="purchase-primary"
          disabled={!stripe || !elements || !elementReady || processing}
        >
          {processing
            ? "Payment processing..."
            : `Pay ${formatMinorAmount(amount, currency)}`}
        </button>
      )}
    </form>
  );
}
